import { logger } from '@/core/logger';
import { useAppStore } from '@/core/stores/appStore';
import { parsePhoneNumber } from '@/core/utils/countryCodes';
import { formatEmiratesId } from '@/core/utils/validation';
import { CustomerDetailsResponse, customerDetailsService } from '@/modules/onboarding/services/customerDetailsService';
import { mapNationalityCodeToValue } from '@/modules/onboarding/utils/nationalityUtils';
import { useCallback, useMemo, useState } from 'react';

/**
 * Custom hook for UAE Pass details screen business logic
 * Handles customer details API call, data comparison, and state management
 */
export const useUaePassDetails = () => {
    // Load UAE Pass registration data from appStore
    const uaePassRegistration = useAppStore((state) => state.uaePassRegistration);
    const user = uaePassRegistration?.user || null;

    // Salesforce customer details (from API)
    const [salesforceData, setSalesforceData] = useState<CustomerDetailsResponse['data'] | null>(null);
    const [isLoadingDetails, setIsLoadingDetails] = useState(false);
    const [detailsError, setDetailsError] = useState<string | null>(null);
    const [shouldSkipOTP, setShouldSkipOTP] = useState(false);
    const [emailMatches, setEmailMatches] = useState(false);
    const [phoneMatches, setPhoneMatches] = useState(false);

    /**
     * Utility to mask a string keeping only the first few characters visible
     */
    const maskValue = useCallback((value: string): string => {
        if (!value) return '';
        const len = value.length;
        const first = value.slice(0, Math.min(3, len));
        const last = value.slice(-1);
        const maskedCount = Math.max(0, len - first.length - 1);
        return maskedCount > 0 ? first + '*'.repeat(maskedCount) + last : first + (len > 3 ? last : '');
    }, []);

    /**
     * Parse country code and local number from phone string
     * Uses centralized country codes utility as single source of truth
     */
    const parsePhone = useCallback((raw: string) => {
        return parsePhoneNumber(raw);
    }, []);

    /**
     * Use Salesforce data for display if available, otherwise use UAE Pass data
     */
    const displayFirstName = useMemo(() => salesforceData?.first_name || user?.firstnameEN || '', [salesforceData?.first_name, user?.firstnameEN]);
    const displayMiddleName = useMemo(() => salesforceData?.middle_name || '', [salesforceData?.middle_name]);
    const displayLastName = useMemo(() => salesforceData?.last_name || user?.lastnameEN || '', [salesforceData?.last_name, user?.lastnameEN]);
    const displayEmail = useMemo(() => salesforceData?.email || user?.email || '', [salesforceData?.email, user?.email]);

    // For Salesforce phone, assume +971 country code if not present
    // For UAE Pass phone, use the parsed phone from mobile field
    const displayPhone = useMemo(() => {
        if (salesforceData?.phone) {
            return salesforceData.phone.startsWith('+971')
                ? salesforceData.phone
                : `+971${salesforceData.phone.replace(/^\+?971/, '')}`;
        }
        return user?.mobile || '';
    }, [salesforceData?.phone, user?.mobile]);

    const parsedPhone = useMemo(() => parsePhone((displayPhone ?? '').replace(/\s+/g, '')), [displayPhone, parsePhone]);

    // Masked values for display
    const maskedFirstName = useMemo(() => maskValue(displayFirstName), [displayFirstName, maskValue]);
    const maskedMiddleName = useMemo(() => maskValue(displayMiddleName), [displayMiddleName, maskValue]);
    const maskedLastName = useMemo(() => maskValue(displayLastName), [displayLastName, maskValue]);
    const maskedEmail = useMemo(() => maskValue(displayEmail), [displayEmail, maskValue]);
    const maskedPhone = useMemo(() => maskValue(parsedPhone.local), [parsedPhone.local, maskValue]);

    /**
     * Fetch customer details from API
     * CRITICAL: This function must NOT depend on user fields in dependencies
     * to prevent infinite loops. Read user data directly from store inside the function.
     */
    const fetchCustomerDetails = useCallback(async (): Promise<void> => {
        // Read current state from store (not from dependencies)
        const currentState = useAppStore.getState();
        const currentUser = currentState.uaePassRegistration?.user;

        if (!currentUser?.idn || !currentUser?.nationalityEN) {
            logger.warn('useUaePassDetails', 'Missing EID or nationality, cannot fetch customer details');
            return;
        }

        setIsLoadingDetails(true);
        setDetailsError(null);

        try {
            // Map nationality code to value (e.g., "IND" -> "Indian")
            const nationalityValue = mapNationalityCodeToValue(currentUser.nationalityEN);

            // Format EID: remove dashes if present, then format as XXX-XXXX-XXXXXXX-X
            const eidValue = formatEmiratesId(currentUser.idn);

            logger.info('useUaePassDetails', 'Fetching customer details', {
                idType: 'EID',
                idValue: eidValue,
                nationality: nationalityValue,
            });

            const response = await customerDetailsService.getCustomerDetails({
                idType: 'EID',
                idValue: eidValue,
                nationality: nationalityValue,
            });

            logger.info('useUaePassDetails', 'Customer details fetched', response);
            setSalesforceData(response.data);

            // CRITICAL: Save Salesforce data to appStore so it's available for OTP verification
            // Update user data with Salesforce phone and email (these are the source of truth)
            const currentState = useAppStore.getState();
            const currentRegistration = currentState.uaePassRegistration;

            if (currentRegistration?.user) {
                // Ensure phone has country code prefix
                let sfPhone = response.data.phone || '';
                if (sfPhone && !sfPhone.startsWith('+')) {
                    // If no country code, assume +971 (UAE)
                    sfPhone = `+971${sfPhone.replace(/^\+?971/, '')}`;
                }

                // Update appStore with Salesforce data
                const updatedRegistrationId = response.data.registration_id || currentRegistration.registrationId;
                const updatedCustomerId = response.data.customer_id || currentRegistration.customerId;

                currentState.setUaePassRegistration({
                    user: {
                        ...currentRegistration.user,
                        mobile: sfPhone || currentRegistration.user.mobile, // Use Salesforce phone, fallback to UAE Pass
                        email: response.data.email || currentRegistration.user.email, // Use Salesforce email, fallback to UAE Pass
                        firstnameEN: response.data.first_name || currentRegistration.user.firstnameEN,
                        lastnameEN: response.data.last_name || currentRegistration.user.lastnameEN,
                    },
                    ...(updatedRegistrationId && { registrationId: updatedRegistrationId }),
                    ...(updatedCustomerId && { customerId: updatedCustomerId }),
                });

                // Also save registration IDs to AsyncStorage for immediate access on app restart
                if (updatedRegistrationId && updatedCustomerId && currentRegistration.user?.idn && currentRegistration.user?.nationalityEN) {
                    const { RegistrationStorage } = await import('@/modules/onboarding/registration/storage/registrationStorage');
                    await RegistrationStorage.setRegistrationIds({
                        registrationId: updatedRegistrationId,
                        customerId: updatedCustomerId,
                        idn: currentRegistration.user.idn,
                        nationality: currentRegistration.user.nationalityEN,
                    }).catch((error) => {
                        logger.error('useUaePassDetails', 'Failed to save registration IDs to storage', error);
                    });
                }

                logger.info('useUaePassDetails', 'Saved Salesforce data to appStore', {
                    phone: sfPhone,
                    email: response.data.email,
                    customerId: response.data.customer_id,
                });
            }

            // Compare Salesforce data with UAE Pass data
            const uaePassEmail = (currentUser.email || '').toLowerCase().trim();
            const uaePassPhone = (currentUser.mobile || '').replace(/\s+/g, '').trim();
            const uaePassFirstName = (currentUser.firstnameEN || '').toLowerCase().trim();
            const uaePassLastName = (currentUser.lastnameEN || '').toLowerCase().trim();

            const sfEmail = (response.data.email || '').toLowerCase().trim();
            const sfPhone = (response.data.phone || '').replace(/\s+/g, '').trim();
            const sfFirstName = (response.data.first_name || '').toLowerCase().trim();
            const sfLastName = (response.data.last_name || '').toLowerCase().trim();

            // Check if fields match
            const emailMatch = uaePassEmail === sfEmail;
            const phoneMatch = uaePassPhone === sfPhone;
            const nameMatches = uaePassFirstName === sfFirstName && uaePassLastName === sfLastName;

            // Store individual match flags
            setEmailMatches(emailMatch);
            setPhoneMatches(phoneMatch);

            // If all fields match, skip OTP verification entirely
            if (emailMatch && phoneMatch && nameMatches) {
                logger.info('useUaePassDetails', 'All fields match, skipping OTP verification');
                setShouldSkipOTP(true);
            } else {
                logger.info('useUaePassDetails', 'Fields do not match, OTP verification required', {
                    emailMatches: emailMatch,
                    phoneMatches: phoneMatch,
                    nameMatches,
                });
                setShouldSkipOTP(false);
            }
        } catch (error: any) {
            logger.error('useUaePassDetails', 'Failed to fetch customer details', error);

            // Handle validation errors
            const apiErrors = error?.response?.data?.errors;
            if (apiErrors && Array.isArray(apiErrors) && apiErrors.length > 0) {
                const errorMessages = apiErrors.map((e: any) => e.message).join(', ');
                setDetailsError(errorMessages);
            } else {
                // If customer not found or other error, proceed with OTP verification
                setDetailsError(null);
                setShouldSkipOTP(false);
                setEmailMatches(false);
                setPhoneMatches(false);
                logger.info('useUaePassDetails', 'Customer details not found, proceeding with OTP verification');
            }
        } finally {
            setIsLoadingDetails(false);
        }
    }, []); // No dependencies - read from store inside function

    const updateLocalSalesforceData = useCallback(
        (updates: Partial<CustomerDetailsResponse['data']>) => {
            setSalesforceData((prev) => (prev ? { ...prev, ...updates } : prev));
        },
        [],
    );

    return {
        // User data
        user,

        // Salesforce data
        salesforceData,
        isLoadingDetails,
        detailsError,
        shouldSkipOTP,
        emailMatches,
        phoneMatches,

        // Display values (prioritize Salesforce over UAE Pass)
        displayFirstName,
        displayMiddleName,
        displayLastName,
        displayEmail,
        displayPhone,

        // Masked values for UI
        maskedFirstName,
        maskedMiddleName,
        maskedLastName,
        maskedEmail,
        maskedPhone,

        // Parsed phone
        parsedPhone,

        // Actions
        fetchCustomerDetails,
        setDetailsError,
        updateLocalSalesforceData,
    };
};

